/**
 * Redemption Management Tab
 *
 * Admin interface for managing store redemptions - view orders, fulfill, cancel, track shipping.
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Search,
  Filter,
  Truck,
  Mail,
  User,
  MapPin,
  Phone,
  Calendar,
  DollarSign,
  X,
  RefreshCw,
  AlertTriangle,
  Copy,
  ExternalLink,
} from "lucide-react";
import {
  useStoreRedemptions,
  useFulfillRedemption,
  useCancelRedemption,
  useUpdateRedemptionStatus,
} from "../../hooks/useFirebaseData";
import { StoreRedemption, RedemptionStatus } from "../../types";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_CONFIG: Record<RedemptionStatus, { label: string; color: string; icon: React.ElementType; bgColor: string }> = {
  pending: { label: "Pending", color: "text-amber-600", icon: Clock, bgColor: "bg-amber-100 dark:bg-amber-900/30" },
  processing: { label: "Processing", color: "text-blue-600", icon: Package, bgColor: "bg-blue-100 dark:bg-blue-900/30" },
  shipped: { label: "Shipped", color: "text-purple-600", icon: Truck, bgColor: "bg-purple-100 dark:bg-purple-900/30" },
  delivered: { label: "Delivered", color: "text-teal-600", icon: MapPin, bgColor: "bg-teal-100 dark:bg-teal-900/30" },
  fulfilled: { label: "Fulfilled", color: "text-green-600", icon: CheckCircle, bgColor: "bg-green-100 dark:bg-green-900/30" },
  cancelled: { label: "Cancelled", color: "text-red-600", icon: XCircle, bgColor: "bg-red-100 dark:bg-red-900/30" },
};

// Valid status transitions
const VALID_TRANSITIONS: Record<RedemptionStatus, RedemptionStatus[]> = {
  pending: ["processing", "shipped", "fulfilled", "cancelled"],
  processing: ["shipped", "fulfilled", "cancelled"],
  shipped: ["delivered", "fulfilled", "cancelled"],
  delivered: ["fulfilled"],
  fulfilled: [],
  cancelled: [],
};

// Tracking carriers
const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];

// Status Change interface
interface StatusChange {
  status: RedemptionStatus;
  timestamp: number;
  changedBy: string;
  message?: string;
}

// Status Timeline Component
const StatusTimeline: React.FC<{ statusHistory?: StatusChange[] }> = ({ statusHistory }) => {
  if (!statusHistory || statusHistory.length === 0) {
    return <p className="text-sm text-gray-500 py-2">No status history available</p>;
  }

  const sortedHistory = [...statusHistory].sort((a, b) => a.timestamp - b.timestamp);

  return (
    <div className="space-y-3">
      {sortedHistory.map((entry, index) => {
        const status = STATUS_CONFIG[entry.status];
        const StatusIcon = status?.icon || Clock;
        const date = new Date(entry.timestamp);

        return (
          <div key={index} className="flex gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${status?.bgColor || 'bg-gray-100'}`}>
              <StatusIcon size={14} className={status?.color || 'text-gray-500'} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className={`font-medium text-sm ${status?.color || 'text-gray-600'}`}>
                  {status?.label || entry.status}
                </span>
                <span className="text-xs text-gray-400">
                  {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {entry.message && (
                <p className="text-sm text-gray-500 mt-0.5">{entry.message}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Update Status Modal
interface UpdateStatusModalProps {
  redemption: StoreRedemption | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateStatus: (data: {
    status: RedemptionStatus;
    message?: string;
    trackingNumber?: string;
    trackingCarrier?: string;
    trackingUrl?: string;
    fulfillmentCode?: string;
  }) => Promise<void>;
}

const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({ redemption, isOpen, onClose, onUpdateStatus }) => {
  const [newStatus, setNewStatus] = useState<RedemptionStatus | "">("");
  const [message, setMessage] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [fulfillmentCode, setFulfillmentCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  React.useEffect(() => {
    if (redemption && isOpen) {
      setNewStatus("");
      setMessage("");
      setTrackingNumber(redemption.trackingNumber || "");
      setTrackingCarrier(redemption.trackingCarrier || "");
      setTrackingUrl(redemption.trackingUrl || "");
      setFulfillmentCode(redemption.fulfillmentCode || "");
    }
  }, [redemption, isOpen]);

  const availableTransitions = redemption ? VALID_TRANSITIONS[redemption.status] || [] : [];

  const handleSubmit = async () => {
    if (!newStatus) return;

    setIsSubmitting(true);
    try {
      await onUpdateStatus({
        status: newStatus,
        message: message || undefined,
        trackingNumber: trackingNumber || undefined,
        trackingCarrier: trackingCarrier || undefined,
        trackingUrl: trackingUrl || undefined,
        fulfillmentCode: fulfillmentCode || undefined,
      });
      onClose();
    } catch (error) {
      console.error("Update status failed:", error);
      alert("Failed to update status");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !redemption) return null;

  const currentStatus = STATUS_CONFIG[redemption.status];
  const CurrentStatusIcon = currentStatus?.icon || Clock;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
            <h2 className="text-lg font-bold">Update Order Status</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Order Info */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="font-medium">{redemption.itemName}</p>
              <p className="text-sm text-gray-500">Customer: {redemption.username} ({redemption.email})</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-gray-500">Current Status:</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${currentStatus?.bgColor} ${currentStatus?.color} flex items-center gap-1`}>
                  <CurrentStatusIcon size={12} />
                  {currentStatus?.label}
                </span>
              </div>
            </div>

            {/* Status History */}
            {redemption.statusHistory && redemption.statusHistory.length > 0 && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium mb-2 flex items-center gap-1">
                  <Clock size={14} />
                  Status History
                </p>
                <StatusTimeline statusHistory={redemption.statusHistory} />
              </div>
            )}

            {availableTransitions.length === 0 ? (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-yellow-700">
                <p className="text-sm">No status transitions available for this order.</p>
              </div>
            ) : (
              <>
                {/* New Status Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    New Status *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableTransitions.map((status) => {
                      const statusConfig = STATUS_CONFIG[status];
                      const StatusIcon = statusConfig?.icon || Clock;
                      const isSelected = newStatus === status;

                      return (
                        <button
                          key={status}
                          onClick={() => setNewStatus(status)}
                          className={`px-3 py-2 rounded-lg border-2 transition-colors flex items-center gap-2 ${
                            isSelected
                              ? `border-current ${statusConfig?.color} ${statusConfig?.bgColor}`
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <StatusIcon size={16} className={isSelected ? statusConfig?.color : 'text-gray-400'} />
                          <span className={`text-sm font-medium ${isSelected ? statusConfig?.color : ''}`}>
                            {statusConfig?.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Message to Customer */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Message to Customer (Optional)
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none resize-none"
                    rows={2}
                    placeholder="This message will be shown to the customer"
                  />
                </div>

                {/* Tracking Info for Shipped Status */}
                {newStatus === "shipped" && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-purple-600 font-medium">
                      <Truck size={16} />
                      <span>Tracking Information</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Carrier
                        </label>
                        <select
                          value={trackingCarrier}
                          onChange={(e) => setTrackingCarrier(e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                        >
                          <option value="">Select carrier</option>
                          {CARRIERS.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Tracking Number
                        </label>
                        <input
                          type="text"
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                          placeholder="Enter tracking #"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Tracking URL (Optional)
                      </label>
                      <input
                        type="text"
                        value={trackingUrl}
                        onChange={(e) => setTrackingUrl(e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                )}

                {/* Fulfillment Code for Fulfilled Status */}
                {newStatus === "fulfilled" && redemption.redemptionType === "code" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Fulfillment Code
                    </label>
                    <input
                      type="text"
                      value={fulfillmentCode}
                      onChange={(e) => setFulfillmentCode(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                      placeholder="Enter code to deliver to customer"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex gap-3 p-4 border-t dark:border-gray-800">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !newStatus}
              className={`flex-1 px-4 py-2.5 font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                newStatus && STATUS_CONFIG[newStatus]
                  ? `${STATUS_CONFIG[newStatus].bgColor} ${STATUS_CONFIG[newStatus].color} hover:opacity-80`
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Update Status
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

interface FulfillModalProps {
  redemption: StoreRedemption;
  isOpen: boolean;
  onClose: () => void;
  onFulfill: (data: { fulfillmentCode?: string; trackingNumber?: string; trackingCarrier?: string; adminNotes?: string }) => Promise<void>;
}

const FulfillModal: React.FC<FulfillModalProps> = ({ redemption, isOpen, onClose, onFulfill }) => {
  const [fulfillmentCode, setFulfillmentCode] = useState(redemption.fulfillmentCode || "");
  const [trackingNumber, setTrackingNumber] = useState(redemption.trackingNumber || "");
  const [trackingCarrier, setTrackingCarrier] = useState(redemption.trackingCarrier || "");
  const [adminNotes, setAdminNotes] = useState(redemption.adminNotes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPhysical = redemption.redemptionType === "physical";
  const isCode = redemption.redemptionType === "code";

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onFulfill({
        fulfillmentCode: isCode ? fulfillmentCode : undefined,
        trackingNumber: isPhysical ? trackingNumber : undefined,
        trackingCarrier: isPhysical ? trackingCarrier : undefined,
        adminNotes: adminNotes || undefined,
      });
      onClose();
    } catch (error) {
      console.error("Fulfill failed:", error);
      alert("Failed to fulfill order");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
            <h2 className="text-lg font-bold">Fulfill Order</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Order Info */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="font-medium">{redemption.itemName}</p>
              <p className="text-sm text-gray-500">Order #{redemption.redemptionId.slice(-8)} • {redemption.gtSpent} GT</p>
              <p className="text-sm text-gray-500">User: {redemption.username} ({redemption.email})</p>
            </div>

            {/* Shipping Info for Physical Items */}
            {isPhysical && redemption.shippingInfo && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={16} className="text-blue-600" />
                  <span className="font-medium text-blue-600">Shipping Address</span>
                </div>
                <p className="text-sm">{redemption.shippingInfo.name}</p>
                <p className="text-sm">{redemption.shippingInfo.address1}</p>
                {redemption.shippingInfo.address2 && <p className="text-sm">{redemption.shippingInfo.address2}</p>}
                <p className="text-sm">{redemption.shippingInfo.city}, {redemption.shippingInfo.state} {redemption.shippingInfo.zip}</p>
                <p className="text-sm">{redemption.shippingInfo.country}</p>
                {redemption.shippingInfo.phone && (
                  <p className="text-sm flex items-center gap-1 mt-1">
                    <Phone size={12} /> {redemption.shippingInfo.phone}
                  </p>
                )}
              </div>
            )}

            {/* Code Input for Code Items */}
            {isCode && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Fulfillment Code *
                </label>
                <input
                  type="text"
                  value={fulfillmentCode}
                  onChange={(e) => setFulfillmentCode(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                  placeholder="Enter redemption code"
                />
              </div>
            )}

            {/* Tracking Info for Physical Items */}
            {isPhysical && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Carrier
                    </label>
                    <select
                      value={trackingCarrier}
                      onChange={(e) => setTrackingCarrier(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                    >
                      <option value="">Select carrier</option>
                      <option value="USPS">USPS</option>
                      <option value="UPS">UPS</option>
                      <option value="FedEx">FedEx</option>
                      <option value="DHL">DHL</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Tracking Number
                    </label>
                    <input
                      type="text"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                      placeholder="Enter tracking #"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Admin Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Admin Notes (Internal)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none resize-none"
                rows={2}
                placeholder="Optional internal notes"
              />
            </div>
          </div>

          <div className="flex gap-3 p-4 border-t dark:border-gray-800">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || (isCode && !fulfillmentCode)}
              className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Fulfilling...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Mark Fulfilled
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

interface CancelModalProps {
  redemption: StoreRedemption;
  isOpen: boolean;
  onClose: () => void;
  onCancel: (reason: string, refundGT: boolean) => Promise<void>;
}

const CancelModal: React.FC<CancelModalProps> = ({ redemption, isOpen, onClose, onCancel }) => {
  const [reason, setReason] = useState("");
  const [refundGT, setRefundGT] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      alert("Please enter a cancellation reason");
      return;
    }

    setIsSubmitting(true);
    try {
      await onCancel(reason, refundGT);
      onClose();
    } catch (error) {
      console.error("Cancel failed:", error);
      alert("Failed to cancel order");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
            <h2 className="text-lg font-bold text-red-600">Cancel Order</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-600">Are you sure?</p>
                <p className="text-sm text-red-600/80">This will cancel the order for {redemption.itemName}.</p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Cancellation Reason *
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
              >
                <option value="">Select a reason</option>
                <option value="Out of stock">Out of stock</option>
                <option value="User requested">User requested cancellation</option>
                <option value="Invalid shipping address">Invalid shipping address</option>
                <option value="Duplicate order">Duplicate order</option>
                <option value="Fraud detected">Fraud detected</option>
                <option value="Item discontinued">Item discontinued</option>
                <option value="Other">Other (specify below)</option>
              </select>
            </div>

            {reason === "Other" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Specify Reason
                </label>
                <textarea
                  value=""
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none resize-none"
                  rows={2}
                  placeholder="Enter reason"
                />
              </div>
            )}

            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <input
                type="checkbox"
                id="refundGT"
                checked={refundGT}
                onChange={(e) => setRefundGT(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="refundGT" className="text-sm">
                Refund <span className="font-bold text-primary">{redemption.gtSpent} GT</span> to user's wallet
              </label>
            </div>
          </div>

          <div className="flex gap-3 p-4 border-t dark:border-gray-800">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              Keep Order
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !reason}
              className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <XCircle size={16} />
                  Cancel Order
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Redemption Row Component
const RedemptionRow: React.FC<{
  redemption: StoreRedemption;
  onFulfill: () => void;
  onCancel: () => void;
  onMarkProcessing: () => void;
}> = ({ redemption, onFulfill, onCancel, onMarkProcessing }) => {
  const status = STATUS_CONFIG[redemption.status];
  const StatusIcon = status.icon;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-start gap-4">
        {/* Status Icon */}
        <div className={`p-2 rounded-lg ${status.bgColor}`}>
          <StatusIcon size={20} className={status.color} />
        </div>

        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold">{redemption.itemName}</h4>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.bgColor} ${status.color}`}>
              {status.label}
            </span>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600">
              {redemption.redemptionType}
            </span>
          </div>

          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <User size={14} />
              {redemption.username}
            </span>
            <span className="flex items-center gap-1">
              <Mail size={14} />
              {redemption.email}
            </span>
            <span className="flex items-center gap-1">
              <DollarSign size={14} />
              {redemption.gtSpent} GT
            </span>
          </div>

          <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
            <span>Order #{redemption.redemptionId.slice(-8)}</span>
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {formatDate(redemption.createdAt)}
            </span>
            {redemption.fulfilledAt && (
              <span className="text-green-600">Fulfilled: {formatDate(redemption.fulfilledAt)}</span>
            )}
          </div>

          {/* Shipping Info Preview */}
          {redemption.shippingInfo && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
              <span className="flex items-center gap-1 text-gray-500">
                <MapPin size={12} />
                {redemption.shippingInfo.city}, {redemption.shippingInfo.state} {redemption.shippingInfo.zip}
              </span>
            </div>
          )}

          {/* Tracking Info */}
          {redemption.trackingNumber && (
            <div className="mt-2 flex items-center gap-2">
              <Truck size={14} className="text-blue-600" />
              <span className="text-sm font-medium">{redemption.trackingCarrier}: </span>
              <span className="text-sm">{redemption.trackingNumber}</span>
              <button
                onClick={() => copyToClipboard(redemption.trackingNumber!)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <Copy size={12} />
              </button>
            </div>
          )}

          {/* Fulfillment Code */}
          {redemption.fulfillmentCode && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm font-medium">Code: </span>
              <code className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-sm">{redemption.fulfillmentCode}</code>
              <button
                onClick={() => copyToClipboard(redemption.fulfillmentCode!)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <Copy size={12} />
              </button>
            </div>
          )}

          {/* Cancel Reason */}
          {redemption.cancelReason && (
            <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600">
              <span className="font-medium">Cancelled:</span> {redemption.cancelReason}
            </div>
          )}

          {/* Admin Notes */}
          {redemption.adminNotes && (
            <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-700">
              <span className="font-medium">Notes:</span> {redemption.adminNotes}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {redemption.status === "pending" && (
            <>
              <button
                onClick={onMarkProcessing}
                className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
              >
                <RefreshCw size={14} />
                Processing
              </button>
              <button
                onClick={onFulfill}
                className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 text-sm font-medium rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors flex items-center gap-1"
              >
                <CheckCircle size={14} />
                Fulfill
              </button>
              <button
                onClick={onCancel}
                className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 text-sm font-medium rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors flex items-center gap-1"
              >
                <XCircle size={14} />
                Cancel
              </button>
            </>
          )}
          {redemption.status === "processing" && (
            <>
              <button
                onClick={onFulfill}
                className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 text-sm font-medium rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors flex items-center gap-1"
              >
                <CheckCircle size={14} />
                Fulfill
              </button>
              <button
                onClick={onCancel}
                className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 text-sm font-medium rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors flex items-center gap-1"
              >
                <XCircle size={14} />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Tab Component
export const RedemptionManagementTab: React.FC = () => {
  const { user } = useAuth();
  const { data: allRedemptions, isLoading } = useStoreRedemptions();
  const fulfillRedemption = useFulfillRedemption();
  const cancelRedemption = useCancelRedemption();
  const updateStatus = useUpdateRedemptionStatus();

  const [statusFilter, setStatusFilter] = useState<RedemptionStatus | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [fulfillModalRedemption, setFulfillModalRedemption] = useState<StoreRedemption | null>(null);
  const [cancelModalRedemption, setCancelModalRedemption] = useState<StoreRedemption | null>(null);

  // Filter redemptions
  const filteredRedemptions = (allRedemptions || [])
    .filter((r) => {
      const matchesStatus = statusFilter === "all" || r.status === statusFilter;
      const matchesSearch =
        r.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.redemptionId.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => b.createdAt - a.createdAt);

  // Stats
  const stats = {
    total: allRedemptions?.length || 0,
    pending: allRedemptions?.filter((r) => r.status === "pending").length || 0,
    processing: allRedemptions?.filter((r) => r.status === "processing").length || 0,
    shipped: allRedemptions?.filter((r) => r.status === "shipped").length || 0,
    delivered: allRedemptions?.filter((r) => r.status === "delivered").length || 0,
    fulfilled: allRedemptions?.filter((r) => r.status === "fulfilled").length || 0,
    cancelled: allRedemptions?.filter((r) => r.status === "cancelled").length || 0,
  };

  const handleFulfill = async (
    redemption: StoreRedemption,
    data: { fulfillmentCode?: string; trackingNumber?: string; trackingCarrier?: string; adminNotes?: string }
  ) => {
    await fulfillRedemption.mutateAsync({
      redemptionId: redemption.redemptionId,
      fulfilledBy: user?.uid || "admin",
      ...data,
    });
  };

  const handleCancel = async (redemption: StoreRedemption, reason: string, refundGT: boolean) => {
    await cancelRedemption.mutateAsync({
      redemptionId: redemption.redemptionId,
      cancelReason: reason,
      cancelledBy: user?.uid || "admin",
      refundGT,
    });
  };

  const handleMarkProcessing = async (redemption: StoreRedemption) => {
    await updateStatus.mutateAsync({
      redemptionId: redemption.redemptionId,
      status: "processing",
    });
  };

  return (
    <motion.div
      key="redemptions"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* Stats */}
      <div className="card p-5">
        <h3 className="text-lg font-bold mb-4">Order Statistics</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-4">
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package size={16} className="text-primary" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Total</p>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-amber-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Pending</p>
            </div>
            <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw size={16} className="text-blue-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Processing</p>
            </div>
            <p className="text-2xl font-bold text-blue-500">{stats.processing}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Truck size={16} className="text-purple-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Shipped</p>
            </div>
            <p className="text-2xl font-bold text-purple-500">{stats.shipped}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={16} className="text-teal-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Delivered</p>
            </div>
            <p className="text-2xl font-bold text-teal-500">{stats.delivered}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={16} className="text-green-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Fulfilled</p>
            </div>
            <p className="text-2xl font-bold text-green-500">{stats.fulfilled}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <XCircle size={16} className="text-red-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Cancelled</p>
            </div>
            <p className="text-2xl font-bold text-red-500">{stats.cancelled}</p>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h3 className="text-lg font-bold">Orders</h3>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search orders..."
                className="px-3 py-2 pl-9 bg-surface-light dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/50"
              />
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as RedemptionStatus | "all")}
              className="px-3 py-2 bg-surface-light dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending ({stats.pending})</option>
              <option value="processing">Processing ({stats.processing})</option>
              <option value="shipped">Shipped ({stats.shipped})</option>
              <option value="delivered">Delivered ({stats.delivered})</option>
              <option value="fulfilled">Fulfilled ({stats.fulfilled})</option>
              <option value="cancelled">Cancelled ({stats.cancelled})</option>
            </select>
          </div>
        </div>

        {/* Orders List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-primary" />
          </div>
        ) : filteredRedemptions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package size={48} className="mx-auto mb-4 opacity-20" />
            <p>No orders found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRedemptions.map((redemption) => (
              <RedemptionRow
                key={redemption.redemptionId}
                redemption={redemption}
                onFulfill={() => setFulfillModalRedemption(redemption)}
                onCancel={() => setCancelModalRedemption(redemption)}
                onMarkProcessing={() => handleMarkProcessing(redemption)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {fulfillModalRedemption && (
        <FulfillModal
          redemption={fulfillModalRedemption}
          isOpen={!!fulfillModalRedemption}
          onClose={() => setFulfillModalRedemption(null)}
          onFulfill={(data) => handleFulfill(fulfillModalRedemption, data)}
        />
      )}

      {cancelModalRedemption && (
        <CancelModal
          redemption={cancelModalRedemption}
          isOpen={!!cancelModalRedemption}
          onClose={() => setCancelModalRedemption(null)}
          onCancel={(reason, refundGT) => handleCancel(cancelModalRedemption, reason, refundGT)}
        />
      )}
    </motion.div>
  );
};

export default RedemptionManagementTab;
